import React, { useContext, useEffect, useState } from "react";

import { authenticateOauth } from "@mne-authentication/api";
import { SessionContext } from "@mne-authentication/session2";

import Typography from "@mui/material/Typography";

import { useSnackbar } from "@mnemonic/feature-snackbar";
import { FormattedMessage, useIntl } from "@mnemonic/intl";
import {
  ButtonContactUs,
  CardActionsError,
  CardContentError,
  CardError,
  CardTitleError,
  PageAccessDenied,
  PageLayout,
  RemoveSplash,
} from "@mnemonic/mui";
import { ErrorIcon } from "@mnemonic/mui/icons";
import * as ArgusUtils from "@mnemonic/pure-utils/ArgusUtils";
import { getTargetMeta } from "@mnemonic/pure-utils/ArgusUtils";

import { commonMessages } from "../commonMessages";
import {
  buildURLBasedOnSearchValues,
  concatURLStringAndRedirect,
  getAuthorizeParameters,
} from "./UserAuthorization/utils/oAuthHandlerUtils";

export interface IOAuthSearchParams {
  response_type: string;
  client_id: string;
  redirect_uri: string;
  state?: string;
  nonce?: string;
  scope?: string;
}

const URL_PREFIX = "/spa/authentication";

export const OAuthHandler = () => {
  const authorizeParameters = getAuthorizeParameters(window.location.href);
  const apiUrl = ArgusUtils.getApiUrl();
  const { session } = useContext(SessionContext);
  const [errorMessage, setErrorMessage] = useState("");
  const { formatMessage } = useIntl();
  const [accessDeniedResponse, setAccessDeniedResponse] = useState(false);
  const { error } = useSnackbar();

  const opts = {
    apiUrl,
    sessionKey: "",
  };

  if (session) {
    opts.sessionKey = session.info.sessionKey;
  }

  const parametersForAuthentication = {
    clientID: authorizeParameters.client_id,
    nonce: authorizeParameters.nonce,
    scope: authorizeParameters.scope?.split(" "),
    redirectURI: authorizeParameters.redirect_uri,
    responseType: authorizeParameters.response_type,
    state: authorizeParameters.state,
  };
  useEffect(() => {
    authenticateOauth(parametersForAuthentication, opts).then(({ json, response }) => {
      if (response.ok) {
        window.location.href = buildURLBasedOnSearchValues(json?.data, parametersForAuthentication?.responseType);
      }

      if (response.status === 401) {
        const loginPageUrl = `${URL_PREFIX}/login?next=${URL_PREFIX}/oauth/authorize?`;

        // Redirect to login page with nextUrl pointing back with original request
        concatURLStringAndRedirect(loginPageUrl, authorizeParameters);
      }
      if (response.status === 403) setAccessDeniedResponse(true);

      // On unhandled error, show generic error card and error messages from backend in snackbar
      if (!response.ok) {
        setErrorMessage(
          `${response.status} ${response.statusText} ${response.headers.get("Argus-Request-ID") || ""} ${
            json.messages[0].timestamp || ""
          }`,
        );

        json?.messages?.map((em) =>
          error({
            size: "large",
            title: formatMessage(commonMessages.GenericErrorTitle),
            message: `${json?.responseCode}: ${em?.field} ${em?.message}`,
          }),
        );
      }
    });
  }, []);

  return (
    <PageLayout
      PageHeaderProps={{
        content: (
          <Typography variant="h6">
            <FormattedMessage id="login.authentication" defaultMessage="Authentication" />
          </Typography>
        ),
      }}
    >
      {/* On 403 - Access denied */}
      {accessDeniedResponse && (
        <>
          <RemoveSplash />
          <PageAccessDenied contact={getTargetMeta().metaInfo.email} />
        </>
      )}
      {/* On any unhandled errors - Generic error card + error messages in snackbar */}
      {!accessDeniedResponse && errorMessage && (
        <>
          <RemoveSplash />
          <CardError>
            <CardTitleError
              Icon={ErrorIcon}
              colorIcon="error"
              subtitle={formatMessage(commonMessages.GenericErrorTitle)}
            />
            <CardContentError contact={getTargetMeta().metaInfo.email} />
            <CardActionsError>
              <ButtonContactUs
                title={"Error at authentication/oauth/authorize"}
                body={errorMessage}
                email={getTargetMeta().metaInfo.email}
              />
            </CardActionsError>
          </CardError>
        </>
      )}
    </PageLayout>
  );
};
